/*
 * Copyright (c) 2020. American Express Company
 */

import $ from 'jquery';

export interface ConfettiConfig {
    elementCount: number;
    colors: string[];
    width: string;
    height: string;
    angle: number;
    spread: number;
    startVelocity: number;
    decay: number;
    dragFriction: number;
    duration: number;
    stagger: number;
    random: Function;
    delay: number;
}

export default class ConfettiService {
    private static readonly defaults = {
        angle: 90,
        spread: 200,
        startVelocity: 45,
        elementCount: 50,
        width: '10px',
        height: '10px',
        colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
        duration: 5000,
        stagger: 0,
        dragFriction: 0.1,
        random: Math.random
    };

    private static async animate(
        root: HTMLElement,
        fettis: any,
        dragFriction: number,
        decay: number,
        duration: number,
        stagger: number
    ): Promise<void> {
        let startTime: number;

        const updateFetti = (fetti: any, progress: number, dragFriction: number, decay: number): void => {
            (fetti.physics.x as number) += Math.cos(fetti.physics.angle2D) * fetti.physics.velocity;
            (fetti.physics.y as number) += Math.sin(fetti.physics.angle2D) * fetti.physics.velocity;
            (fetti.physics.z as number) += Math.sin(fetti.physics.angle3D) * fetti.physics.velocity;
            (fetti.physics.wobble as number) += fetti.physics.wobbleSpeed as number;
            // Backward compatibility
            if (decay) {
                fetti.physics.velocity *= decay;
            } else {
                fetti.physics.velocity -= fetti.physics.velocity * dragFriction;
            }
            (fetti.physics.y as number) += 3;
            (fetti.physics.tiltAngle as number) += fetti.physics.tiltAngleSpeed as number;

            const { x, y, tiltAngle, wobble } = fetti.physics;
            const wobbleX = (x as number) + 10 * Math.cos(wobble);
            const wobbleY = (y as number) + 10 * Math.sin(wobble);
            const transform = `translate3d(${wobbleX}px, ${wobbleY}px, 0) rotate3d(1, 1, 1, ${tiltAngle}rad)`;

            fetti.element.style.visibility = 'visible';
            fetti.element.style.transform = transform;
            fetti.element.style.opacity = 1 - progress;
        };

        return await new Promise((resolve) => {
            function update(time: number): void {
                if (!startTime) startTime = time;
                const elapsed = time - startTime;
                const progress = startTime === time ? 0 : (time - startTime) / duration;
                fettis.slice(0, Math.ceil(elapsed / stagger)).forEach((fetti: any) => {
                    updateFetti(fetti, progress, dragFriction, decay);
                });

                if (time - startTime < duration) {
                    requestAnimationFrame(update);
                } else {
                    fettis.forEach((fetti: any) => {
                        if (fetti.element.parentNode === root) {
                            return root.removeChild(fetti.element);
                        }
                    });
                    resolve();
                }
            }

            requestAnimationFrame(update);
        });
    }

    private static backwardPatch(config: any): ConfettiConfig {
        if (!config.stagger && config.delay) {
            config.stagger = config.delay;
        }

        return config;
    }

    private static createElements(
        root: HTMLElement,
        elementCount: number,
        colors: string[],
        width: string,
        height: string
    ): HTMLElement[] {
        return Array.from({ length: elementCount }).map((_, index) => {
            const element: HTMLElement = document.createElement('div');
            const color = colors[index % colors.length];
            element.style.backgroundColor = color;
            element.style.width = width;
            element.style.height = height;
            element.style.position = 'absolute';
            element.style.zIndex = '99999999';
            element.style.willChange = 'transform, opacity';
            element.style.visibility = 'hidden';
            element.className = 'fetti';
            root.appendChild(element);
            return element;
        });
    }

    private static randomPhysics(angle: number, spread: number, startVelocity: number, random: Function): any {
        const radAngle = angle * (Math.PI / 180);
        const radSpread = spread * (Math.PI / 180);
        return {
            x: 0,
            y: 0,
            wobble: random() * 10,
            wobbleSpeed: 0.1 + random() * 0.1,
            velocity: startVelocity * 0.5 + random() * startVelocity,
            angle2D: -radAngle + (0.5 * radSpread - random() * radSpread),
            angle3D: -(Math.PI / 4) + random() * (Math.PI / 2),
            tiltAngle: random() * Math.PI,
            tiltAngleSpeed: 0.1 + random() * 0.3
        };
    }

    public static async fire(root: HTMLElement, config = {}): Promise<void> {
        const {
            elementCount,
            colors,
            width,
            height,
            angle,
            spread,
            startVelocity,
            decay,
            dragFriction,
            duration,
            stagger,
            random
        } = Object.assign({}, this.defaults, this.backwardPatch(config));
        const elements: HTMLElement[] = this.createElements(root, elementCount, colors, width, height);
        const fettis = elements.map((element) => ({
            element,
            physics: this.randomPhysics(angle, spread, startVelocity, random)
        }));

        await this.animate(root, fettis, dragFriction, decay, duration, stagger);
        $('.fetti').remove(); // This is not strictly necessary, but ensures cleanup happens no matter what
    }
}
